import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  type UserPreference,
  addUserPreference,
  deleteUserPreference,
  getUserPreferences,
} from '~/services/TripPlannerService';

export const PREFERENCE_TYPES = {
  THEMES: {
    value: 'THEMES',
    label: 'Theme',
  },
  MUST_DO_ACTIVITY: {
    value: 'MUST_DO_ACTIVITY',
    label: 'Must Do Activity',
  },
} as const;

export type PreferenceType = keyof typeof PREFERENCE_TYPES;

export default function Preferences() {
  const { enqueueSnackbar } = useSnackbar();
  const [preferences, setPreferences] = useState<Array<UserPreference>>([]);
  const [filterType, setFilterType] = useState<PreferenceType | ''>('');
  const [filterLocation, setFilterLocation] = useState('');

  // New preference form state
  const [newPreference, setNewPreference] = useState({
    name: '',
    type: PREFERENCE_TYPES.THEMES.value as PreferenceType,
    description: '',
    placeId: '',
  });

  const fetchPreferences = useCallback(async () => {
    try {
      const params: { type?: PreferenceType } = {};
      if (filterType) {
        params.type = filterType;
      }

      const response = await getUserPreferences(params);
      if (response.status === 200) {
        // Filter by location locally
        const filteredPreferences = filterLocation
          ? response.result.filter((pref) =>
              pref.locations.some((loc) => loc.toLowerCase().includes(filterLocation.toLowerCase())),
            )
          : response.result;
        setPreferences(filteredPreferences);
      }
    } catch (error) {
      setPreferences([]);
      enqueueSnackbar('Failed to fetch preferences', { variant: 'error' });
    }
  }, [filterType, filterLocation, enqueueSnackbar]);

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteUserPreference(id);
      if (response.status === 200) {
        await fetchPreferences();
        enqueueSnackbar('Preference deleted successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to delete preference:', error);
      enqueueSnackbar('Failed to delete preference', { variant: 'error' });
    }
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPreference((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPreference((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleFilterLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterLocation(e.target.value);
  };

  const handlePlaceIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewPreference((prev) => ({ ...prev, placeId: e.target.value }));
  };

  const handleTypeChange = (e: SelectChangeEvent<PreferenceType>) => {
    setNewPreference((prev) => ({ ...prev, type: e.target.value as PreferenceType }));
  };

  const handleFilterTypeChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value as PreferenceType | '');
  };

  const handleDeleteClick = (id: string) => () => {
    handleDelete(id);
  };

  const handleSubmitNewPreference = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await addUserPreference(newPreference);
      if (response.status === 200) {
        setNewPreference((prev) => ({
          ...prev,
          name: '',
          description: '',
        }));
        fetchPreferences();
        enqueueSnackbar('Preference added successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to add preference:', error);
      enqueueSnackbar('Failed to add preference', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        User Preferences
      </Typography>
      <Typography variant="h6" gutterBottom>
        - Please make sure location exists before creating a preference
      </Typography>
      <Typography variant="h6" gutterBottom>
        - Go to location tab to create new location and get the placeID
      </Typography>

      {/* Add New Preference Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add New Preference
        </Typography>
        <Box component="form" onSubmit={handleSubmitNewPreference}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField label="Name" required fullWidth value={newPreference.name} onChange={handleNameChange} />
              <FormControl required fullWidth>
                <InputLabel id="new-type-label">Type</InputLabel>
                <Select labelId="new-type-label" value={newPreference.type} label="Type" onChange={handleTypeChange}>
                  {Object.values(PREFERENCE_TYPES).map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="Description"
              fullWidth
              value={newPreference.description}
              onChange={handleDescriptionChange}
            />
            <TextField
              label="Place ID"
              required
              fullWidth
              value={newPreference.placeId}
              onChange={handlePlaceIdChange}
              placeholder="Enter place ID"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" color="primary">
                Add Preference
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={2} mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="type-filter-label">Filter by Type</InputLabel>
          <Select
            labelId="type-filter-label"
            value={filterType}
            label="Filter by Type"
            onChange={handleFilterTypeChange}
          >
            <MenuItem value="">All</MenuItem>
            {Object.values(PREFERENCE_TYPES).map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Location"
          value={filterLocation}
          onChange={handleFilterLocationChange}
          placeholder="Filter by location"
        />
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Place ID</TableCell>
              <TableCell>Locations</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preferences.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No preferences found
                </TableCell>
              </TableRow>
            ) : (
              preferences.map((preference) => (
                <TableRow key={preference.id}>
                  <TableCell>{preference.name}</TableCell>
                  <TableCell>{preference.description || '-'}</TableCell>
                  <TableCell>{PREFERENCE_TYPES[preference.type].label}</TableCell>
                  <TableCell>{preference.placeId}</TableCell>
                  <TableCell>{preference.locations.join(', ') || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete preference">
                      <Button color="error" onClick={handleDeleteClick(preference.id)} size="small">
                        Delete
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
